import { memo, useCallback } from 'react';
import { FlatList, StyleSheet } from 'react-native';
import styled, { useTheme } from 'styled-components/native';

import LoadingView from '../../elements/LoadingView';
import RefreshControl from '../../elements/RefreshControl';
import ReviewItem from '../../elements/ReviewItem';
import { translate } from '../../i18n';
import EmptyReview from './EmptyReview';
import Header from './Header';
import { useData } from './hooks';

interface Props {
  id: string;
}

export default function ItemReviewsPage({ id }: Props) {
  const theme = useTheme();
  const { data, fetchData } = useData(id);
  const {
    currentPage,
    detailLoadingType,
    detailData,
    hasMoreItems,
    isListFetching,
    listData,
  } = data;

  const handleRefresh = useCallback(() => {
    fetchData('reload', 1);
  }, [fetchData]);

  const handleEndReached = useCallback(() => {
    if (!isListFetching && hasMoreItems) {
      fetchData('loadMore', currentPage + 1);
    }
  }, [fetchData, isListFetching, hasMoreItems, currentPage]);

  const renderItem = useCallback(({ item }) => {
    return <ReviewItem {...item} />;
  }, []);

  const renderFooter = useCallback(() => {
    const styles = StyleSheet.create({
      footerLoading: {
        backgroundColor: theme.palette.neutralWhite,
      },
    });

    return (
      <S.Footer>
        {detailLoadingType !== 'reload' && isListFetching && (
          <LoadingView style={styles.footerLoading} />
        )}
        {!isListFetching && !hasMoreItems && (
          <S.FooterText>{translate('end-of-reviews')}</S.FooterText>
        )}
      </S.Footer>
    );
  }, [detailLoadingType, hasMoreItems, isListFetching]);

  return (
    <S.Container>
      <Header data={detailData} />
      {!isListFetching && !listData.length && <EmptyReview />}
      {(isListFetching || listData.length > 0) && (
        <FlatList
          data={listData}
          renderItem={renderItem}
          onEndReached={handleEndReached}
          refreshing={detailLoadingType === 'reload' && isListFetching}
          refreshControl={
            <RefreshControl
              onRefresh={handleRefresh}
              isFetching={isListFetching}
            />
          }
          ItemSeparatorComponent={renderSeparator}
          ListFooterComponent={renderFooter}
        />
      )}
    </S.Container>
  );
}

const renderSeparator = memo(() => (
  <S.SeparatorContainer>
    <S.Separator />
  </S.SeparatorContainer>
));

const S = {
  Container: styled.SafeAreaView`
    flex: 1;
  `,
  Footer: styled.View`
    align-items: center;
    padding: 30px 0;
    background-color: ${({ theme }) => theme.palette.neutralWhite};
  `,
  FooterText: styled.Text`
    font-size: 12px;
    line-height: 17px;
    color: ${({ theme }) => theme.palette.neutralDark};
  `,
  SeparatorContainer: styled.View`
    background-color: ${({ theme }) => theme.palette.white};
    padding: 0 15px;
  `,
  Separator: styled.View`
    border-bottom-width: 1px;
    border-color: ${({ theme }) => theme.palette.neutralLight};
  `,
};
